import React, { useState, useEffect } from "react";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import {
  getStorage,
  uploadBytes,
  listAll,
  getDownloadURL,
  ref,
  deleteObject,
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import Papa from "papaparse";
import {
  Cloud,
  Database,
  AlertCircle,
  CheckCircle,
  File,
  RefreshCw,
  Calendar,
  Archive,
  Save,
  Clock,
  Download,
  Trash2,
} from "lucide-react";

const BackupRestore = () => {
  // State declarations
  const [automaticBackupStatus, setAutomaticBackupStatus] = useState("idle");
  const [manualBackupStatus, setManualBackupStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [automaticProgress, setAutomaticProgress] = useState(0);
  const [manualProgress, setManualProgress] = useState(0);
  const [lastAutomaticBackup, setLastAutomaticBackup] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [lastManualBackup, setLastManualBackup] = useState(null);
  const [automaticBackupFiles, setAutomaticBackupFiles] = useState([]);
  const [manualBackupFiles, setManualBackupFiles] = useState([]);
  const [backupInterval, setBackupInterval] = useState("1");
  const [backupTime, setBackupTime] = useState("02:00");
  const [nextScheduledBackup, setNextScheduledBackup] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const db = getFirestore();
  const storage = getStorage();
  const auth = getAuth();

  const BACKUP_PATHS = {
    automatic: "Backup",
    manual: "Backup2",
  };

  // Updated collections array to include all collections from your Firestore rules
  const collections = [
    { name: "Sales", filename: "sales_backup.csv" },
    { name: "Operations", filename: "operations_backup.csv" },
    { name: "Ads", filename: "ads_backup.csv" },
    { name: "Products", filename: "products_backup.csv" },
    { name: "Designs", filename: "designs_backup.csv" },
    { name: "Educhecker", filename: "educhecker_backup.csv" },
    { name: "Daily-target", filename: "daily_target_backup.csv" },
    { name: "ProductVariants", filename: "product_variants_backup.csv" },
    { name: "TransactionStates", filename: "transaction_states_backup.csv" },
    { name: "Settings", filename: "settings_backup.csv" },
  ];

  const backupIntervals = [
    { value: "1", label: "Every day" },
    { value: "2", label: "Every 2 days" },
    { value: "3", label: "Every 3 days" },
    { value: "7", label: "Every week" },
    { value: "14", label: "Every 2 weeks" },
    { value: "30", label: "Every month" },
  ];

  // Initialize component
  useEffect(() => {
    const initialize = async () => {
      try {
        await ensureAuthenticated();
        await loadSettings();
        await fetchExistingBackups();
        setupAutomaticBackup();
        setIsInitialized(true);
      } catch (error) {
        console.error("Initialization error:", error);
        setError("Failed to initialize backup system");
      }
    };

    initialize();

    return () => {
      if (window.backupInterval) clearInterval(window.backupInterval);
      if (window.backupTimeout) clearTimeout(window.backupTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure authentication
  const ensureAuthenticated = async () => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error("Authentication required");
    }
    try {
      await user.getIdToken(true);
      return true;
    } catch (error) {
      throw new Error("Authentication expired - please login again");
    }
  };

  // Load backup settings
  const loadSettings = async () => {
    try {
      const settingsRef = doc(db, "Settings", "backupSettings");
      const settingsDoc = await getDoc(settingsRef);

      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setBackupInterval(data.backupInterval || "1");
        setBackupTime(data.backupTime || "02:00");
      } else {
        await setDoc(settingsRef, {
          backupInterval: "1",
          backupTime: "02:00",
          createdAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      throw error;
    }
  };

  // Setup automatic backup
  const setupAutomaticBackup = () => {
    if (window.backupInterval) clearInterval(window.backupInterval);
    if (window.backupTimeout) clearTimeout(window.backupTimeout);

    const now = new Date();
    const [hours, minutes] = backupTime.split(":");
    let nextBackup = new Date();
    nextBackup.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    if (now > nextBackup) {
      nextBackup.setDate(nextBackup.getDate() + parseInt(backupInterval));
    }

    const timeUntilBackup = nextBackup - now;
    setNextScheduledBackup(nextBackup.toLocaleString());

    window.backupTimeout = setTimeout(() => {
      handleAutomaticBackup();
      window.backupInterval = setInterval(
        handleAutomaticBackup,
        parseInt(backupInterval) * 24 * 60 * 60 * 1000
      );
    }, timeUntilBackup);
  };

  // Improved fetchCollectionData function
  const fetchCollectionData = async (collectionName) => {
    try {
      const snapshot = await getDocs(collection(db, collectionName));

      // Check if collection is empty
      if (snapshot.empty) {
        console.log(`Collection ${collectionName} is empty`);
        return []; // Return empty array for empty collections
      }

      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error(`Error fetching collection ${collectionName}:`, error);
      // Return empty array on error but don't stop the entire backup process
      return [];
    }
  };

  // Improved CSV conversion
  const convertToCSV = (data) => {
    // Pre-process data to handle complex objects
    const processedData = data.map((item) => {
      const processed = {};

      // Process each field in the item
      Object.entries(item).forEach(([key, value]) => {
        // Handle arrays by converting to JSON strings
        if (Array.isArray(value)) {
          processed[key] = JSON.stringify(value);
        }
        // Handle objects by converting to JSON strings
        else if (value !== null && typeof value === "object") {
          // Check if it's a Firestore timestamp and handle specially
          if (
            value?.seconds !== undefined &&
            value?.nanoseconds !== undefined
          ) {
            try {
              // Try to convert to a date string
              const date = new Date(value.seconds * 1000);
              processed[key] = date.toISOString();
            } catch (e) {
              // If that fails, just stringify
              processed[key] = JSON.stringify(value);
            }
          } else {
            processed[key] = JSON.stringify(value);
          }
        }
        // Pass through primitive values
        else {
          processed[key] = value;
        }
      });

      return processed;
    });

    return Papa.unparse(processedData, {
      quotes: true,
      header: true,
      encoding: "UTF-8",
    });
  };

  // Upload to storage
  const uploadToStorage = async (blob, filename, folder) => {
    const backupRef = ref(storage, `${folder}/${filename}`);
    await uploadBytes(backupRef, blob);
  };

  // Improved automatic backup with better error handling
  const handleAutomaticBackup = async () => {
    try {
      await ensureAuthenticated();
      setAutomaticBackupStatus("processing");
      setError(null);
      setAutomaticProgress(0);

      const successfulBackups = [];
      const failedBackups = [];

      for (let i = 0; i < collections.length; i++) {
        const collection = collections[i];
        try {
          setAutomaticProgress((i / collections.length) * 100);
          console.log(`Backing up ${collection.name}...`);

          const data = await fetchCollectionData(collection.name);
          const date = new Date().toISOString().split("T")[0];
          const time = new Date()
            .toTimeString()
            .split(" ")[0]
            .replace(/:/g, "-");
          const filename = `${collection.name}_${date}_${time}.csv`;

          // Only proceed if we have data
          if (data && data.length > 0) {
            const csv = convertToCSV(data);
            const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csv], {
              type: "text/csv;charset=utf-8;",
            });

            await uploadToStorage(blob, filename, BACKUP_PATHS.automatic);
            successfulBackups.push(collection.name);
          } else {
            console.log(`Skipping empty collection: ${collection.name}`);
            // We still consider it successful since we handled it properly
            successfulBackups.push(`${collection.name} (empty)`);
          }
        } catch (collectionError) {
          console.error(
            `Error backing up ${collection.name}:`,
            collectionError
          );
          failedBackups.push(collection.name);
          // Continue with other collections rather than stopping the whole process
        }

        // Update progress regardless of success/failure
        setAutomaticProgress(((i + 1) / collections.length) * 100);
      }

      // Set appropriate status based on results
      if (failedBackups.length === 0) {
        setAutomaticBackupStatus("completed");
        setLastAutomaticBackup(new Date().toISOString());
        setSuccess(
          `Automatic backup completed successfully for all ${successfulBackups.length} collections`
        );
      } else {
        setAutomaticBackupStatus("partial");
        setLastAutomaticBackup(new Date().toISOString());
        setError(
          `Automatic backup completed with some issues. Failed: ${failedBackups.join(
            ", "
          )}`
        );
      }

      await fetchExistingBackups();
    } catch (error) {
      console.error("Automatic backup failed:", error);
      setError(`Automatic backup failed: ${error.message}`);
      setAutomaticBackupStatus("failed");
    }
  };

  // Improved manual backup with better error handling
  const handleManualBackup = async () => {
    try {
      await ensureAuthenticated();
      setManualBackupStatus("processing");
      setError(null);
      setManualProgress(0);

      const successfulBackups = [];
      const failedBackups = [];

      for (let i = 0; i < collections.length; i++) {
        const collection = collections[i];
        try {
          setManualProgress((i / collections.length) * 100);
          console.log(`Backing up ${collection.name}...`);

          const data = await fetchCollectionData(collection.name);
          const date = new Date().toISOString().split("T")[0];
          const time = new Date()
            .toTimeString()
            .split(" ")[0]
            .replace(/:/g, "-");
          const filename = `${collection.name}_${date}_${time}.csv`;

          // Only proceed if we have data
          if (data && data.length > 0) {
            const csv = convertToCSV(data);
            const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csv], {
              type: "text/csv;charset=utf-8;",
            });

            await uploadToStorage(blob, filename, BACKUP_PATHS.manual);
            successfulBackups.push(collection.name);
          } else {
            console.log(`Skipping empty collection: ${collection.name}`);
            // We still consider it successful since we handled it properly
            successfulBackups.push(`${collection.name} (empty)`);
          }
        } catch (collectionError) {
          console.error(
            `Error backing up ${collection.name}:`,
            collectionError
          );
          failedBackups.push(collection.name);
          // Continue with other collections rather than stopping the whole process
        }

        // Update progress regardless of success/failure
        setManualProgress(((i + 1) / collections.length) * 100);
      }

      // Set appropriate status based on results
      if (failedBackups.length === 0) {
        setManualBackupStatus("completed");
        setLastManualBackup(new Date().toISOString());
        setSuccess(
          `Manual backup completed successfully for all ${successfulBackups.length} collections`
        );
      } else {
        setManualBackupStatus("partial");
        setLastManualBackup(new Date().toISOString());
        setError(
          `Manual backup completed with some issues. Failed: ${failedBackups.join(
            ", "
          )}`
        );
      }

      await fetchExistingBackups();
    } catch (error) {
      console.error("Manual backup failed:", error);
      setError(`Manual backup failed: ${error.message}`);
      setManualBackupStatus("failed");
    }
  };

  // Fetch existing backups
  const fetchExistingBackups = async () => {
    try {
      const autoRef = ref(storage, BACKUP_PATHS.automatic);
      const manualRef = ref(storage, BACKUP_PATHS.manual);

      const [autoFiles, manualFiles] = await Promise.all([
        listAll(autoRef),
        listAll(manualRef),
      ]);

      const processBackupFiles = async (files) => {
        const processedFiles = await Promise.all(
          files.items.map(async (item) => {
            try {
              // Extract parts from filename
              const parts = item.name.split("_");

              // Get collection name (first part)
              const collectionName = parts[0];

              // Get date - second part, fallback to current date if missing
              const dateStr =
                parts[1] || new Date().toISOString().split("T")[0];

              // Get time - third part (remove .csv extension)
              let timeStr = parts[2]?.split(".")[0] || "00-00-00";

              // Format time properly
              const formattedTime = timeStr.replace(/-/g, ":");

              // Create a valid date object, with better validation
              let formattedDate;
              let displayDate;

              try {
                // Validate date string format (handles YYYY-MM-DD format)
                if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
                  // Check if time is in correct format (HH-MM-SS or HH:MM:SS)
                  const timePattern = /^(\d{2})[-:](\d{2})[-:](\d{2})$/;
                  const timeMatch = timePattern.exec(timeStr);

                  if (timeMatch) {
                    // Normalize time format to HH:MM:SS
                    const normalizedTime = `${timeMatch[1]}:${timeMatch[2]}:${timeMatch[3]}`;

                    // Create date object with proper format
                    formattedDate = new Date(`${dateStr}T${normalizedTime}`);

                    // Additional validation
                    if (isNaN(formattedDate.getTime())) {
                      throw new Error("Invalid date time combination");
                    }
                  } else {
                    // If time format is invalid, create date with just the date part
                    formattedDate = new Date(`${dateStr}T00:00:00`);

                    if (isNaN(formattedDate.getTime())) {
                      throw new Error("Invalid date");
                    }
                  }

                  // Format the date for display
                  displayDate = formattedDate.toLocaleString();
                } else {
                  // Handle other date formats or situations
                  throw new Error("Unsupported date format");
                }
              } catch (e) {
                console.log(`Date parsing error for ${item.name}:`, e.message);
                // Set fallbacks
                formattedDate = new Date(); // Current date as fallback for sorting

                // Create a more helpful fallback display string
                try {
                  // Try to at least get a meaningful date representation
                  const year = dateStr.substring(0, 4);
                  const month = dateStr.substring(5, 7);
                  const day = dateStr.substring(8, 10);

                  if (year && month && day) {
                    displayDate = `${year}-${month}-${day} ${timeStr.replace(
                      /-/g,
                      ":"
                    )}`;
                  } else {
                    displayDate = "Unknown Date";
                  }
                } catch (fallbackError) {
                  displayDate = "Unknown Date";
                }
              }

              return {
                name: item.name,
                path: item.fullPath,
                date: dateStr,
                time: formattedTime,
                formattedDate: formattedDate,
                displayDate: displayDate,
                collectionName: collectionName,
                url: await getDownloadURL(item),
                ref: item,
              };
            } catch (error) {
              console.error(
                `Error processing backup file ${item.name}:`,
                error
              );
              return {
                name: item.name,
                path: item.fullPath,
                displayDate: "Unknown Date",
                url: await getDownloadURL(item),
                ref: item,
              };
            }
          })
        );

        return processedFiles
          .filter((file) => file !== null)
          .sort((a, b) => {
            // If formattedDate exists for both, sort by that
            if (a.formattedDate && b.formattedDate) {
              return b.formattedDate - a.formattedDate;
            }
            // Otherwise just keep the order
            return 0;
          });
      };

      const [automaticFiles, manualBackups] = await Promise.all([
        processBackupFiles(autoFiles),
        processBackupFiles(manualFiles),
      ]);

      setAutomaticBackupFiles(automaticFiles);
      setManualBackupFiles(manualBackups);
    } catch (error) {
      console.error("Error fetching backups:", error);
      setError("Failed to fetch existing backups");
    }
  };

  // Download backup
  const downloadBackup = async (file, folder) => {
    try {
      setError(null);

      const response = await fetch(file.url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download error:", error);
      setError(`Failed to download backup: ${error.message}`);
    }
  };

  // Delete backup
  const deleteBackup = async (file, folder) => {
    try {
      if (!window.confirm("Are you sure you want to delete this backup?")) {
        return;
      }

      setError(null);
      setSuccess(null);

      // Display a message that deletion is in progress
      setSuccess("Deleting backup...");

      const fileRef = file.ref;

      // Delete the file from storage
      await deleteObject(fileRef);

      // Refresh the backups list
      await fetchExistingBackups();
      setSuccess("Backup deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      setError(`Failed to delete backup: ${error.message}`);
    }
  };

  // Save settings
  const saveBackupSettings = async () => {
    try {
      const settingsRef = doc(db, "Settings", "backupSettings");
      await setDoc(
        settingsRef,
        {
          backupInterval,
          backupTime,
          updatedAt: new Date().toISOString(),
        },
        { merge: true }
      );

      setupAutomaticBackup();
      setSuccess("Backup settings updated successfully");
    } catch (error) {
      console.error("Error saving settings:", error);
      setError(`Failed to save settings: ${error.message}`);
    }
  };

  // Helper function to display status
  const getStatusDisplay = (status) => {
    switch (status) {
      case "completed":
        return <span className="text-green-600">Completed</span>;
      case "processing":
        return <span className="text-blue-600">Processing</span>;
      case "failed":
        return <span className="text-red-600">Failed</span>;
      case "partial":
        return <span className="text-yellow-600">Partially Completed</span>;
      default:
        return <span className="text-gray-600">Idle</span>;
    }
  };

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
      <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
        <Database className="w-8 h-8" />
        Backup & Restore
      </h1>

      {/* Settings Section */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-6 h-6" />
          Backup Settings
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Backup Interval
            </label>
            <select
              value={backupInterval}
              onChange={(e) => setBackupInterval(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
            >
              {backupIntervals.map((interval) => (
                <option key={interval.value} value={interval.value}>
                  {interval.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Backup Time
            </label>
            <input
              type="time"
              value={backupTime}
              onChange={(e) => setBackupTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
            />
          </div>
        </div>

        <button
          onClick={saveBackupSettings}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <Save className="w-4 h-4" />
          Save Settings
        </button>

        {nextScheduledBackup && (
          <div className="mt-4 text-sm text-gray-600 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Next backup scheduled for: {nextScheduledBackup}
          </div>
        )}
      </div>

      {/* Status Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 text-green-600 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Manual Backup Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Archive className="w-5 h-5" />
            Manual Backup
          </h2>

          <button
            onClick={handleManualBackup}
            disabled={manualBackupStatus === "processing"}
            className="w-full mb-4 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors"
          >
            {manualBackupStatus === "processing" ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Backing up... ({Math.round(manualProgress)}%)
              </>
            ) : (
              <>
                <Cloud className="w-5 h-5" />
                Create Manual Backup
              </>
            )}
          </button>

          {/* Manual Progress Bar */}
          {manualBackupStatus !== "idle" && (
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
              <div
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${manualProgress}%` }}
              />
            </div>
          )}

          {/* Manual Backup List */}
          <div className="mt-6">
            <h3 className="font-medium mb-3 text-gray-700">
              Recent Manual Backups
            </h3>
            <div className="space-y-3">
              {manualBackupFiles.length > 0 ? (
                manualBackupFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <File className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          {file.name.split("_")[0]}
                        </p>
                        <p className="text-xs text-gray-500">
                          {file.displayDate || "Unknown Date"}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          downloadBackup(file, BACKUP_PATHS.manual)
                        }
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                        title="Download backup"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteBackup(file, BACKUP_PATHS.manual)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Delete backup"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No manual backups found
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Automatic Backup Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Automatic Backup Status
          </h2>

          {/* Status Card */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Status:</span>
              {getStatusDisplay(automaticBackupStatus)}
            </div>

            {lastAutomaticBackup && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-600">
                  Last Backup:
                </span>
                <span className="text-sm text-gray-600">
                  {new Date(lastAutomaticBackup).toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Automatic Progress Bar */}
          {automaticBackupStatus === "processing" && (
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-6">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${automaticProgress}%` }}
              />
            </div>
          )}

          {/* Automatic Backup List */}
          <div className="mt-6">
            <h3 className="font-medium mb-3 text-gray-700">
              Recent Automatic Backups
            </h3>
            <div className="space-y-3">
              {automaticBackupFiles.length > 0 ? (
                automaticBackupFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <File className="w-4 h-4 text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          {file.name.split("_")[0]}
                        </p>
                        <p className="text-xs text-gray-500">
                          {file.displayDate || "Unknown Date"}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          downloadBackup(file, BACKUP_PATHS.automatic)
                        }
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                        title="Download backup"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          deleteBackup(file, BACKUP_PATHS.automatic)
                        }
                        className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Delete backup"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No automatic backups found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Collection List */}
      <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Database className="w-5 h-5" />
          Collections Included in Backup
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {collections.map((collection, index) => (
            <div
              key={index}
              className="p-3 bg-gray-50 rounded-lg border border-gray-200"
            >
              <p className="font-medium text-gray-700">{collection.name}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Developer Credits */}
      <div className="text-center mt-8 text-sm text-gray-500">
        <p>CreateVista V1.2.7</p>
      </div>
    </div>
  );
};

export default BackupRestore;
